
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { createUniqueFilename } from '../db/adapter/desktopPathUtils';
import { playNotificationSound } from '@/utils/notificationHelper';
import { notifyBackupSuccess, notifyRestoreSuccess } from '@/utils/notificationHelper';

/**
 * Create a backup of all history records
 */
export const createHistoryBackup = async (): Promise<string> => {
  try {
    // Collect all history-related data
    const historyData = {
      adminActions: {},
      teacherHistory: {},
      studentHistory: {},
      communicationLogs: [],
      timestamp: new Date().toISOString(),
      version: '1.0'
    };

    // Get all keys that might contain history data
    const allKeys = Object.keys(localStorage);
    
    // Extract admin actions (from adminActionTracking)
    const adminActionKeys = allKeys.filter(key => key.includes('admin_action_'));
    adminActionKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        historyData.adminActions[key] = JSON.parse(data);
      }
    });
    
    // Extract teacher history
    const teacherHistoryKeys = allKeys.filter(key => key.includes('teacher_history_'));
    teacherHistoryKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        historyData.teacherHistory[key] = JSON.parse(data);
      }
    });
    
    // Extract student history
    const studentHistoryKeys = allKeys.filter(key => key.includes('student_history_'));
    studentHistoryKeys.forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        historyData.studentHistory[key] = JSON.parse(data);
      }
    });
    
    // Get communication logs
    const communicationLogsData = localStorage.getItem('communication_logs');
    if (communicationLogsData) {
      historyData.communicationLogs = JSON.parse(communicationLogsData);
    }

    // Create a JSON string of the backup data
    const backupStr = JSON.stringify(historyData);
    
    // Create a unique ID for this backup
    const backupId = uuidv4();
    
    // Store the backup in localStorage
    const backupKey = `history_backup_${backupId}`;
    localStorage.setItem(backupKey, backupStr);
    
    // Also store metadata for this backup
    const backupsMeta = JSON.parse(localStorage.getItem('history_backups_meta') || '[]');
    const newBackupMeta = {
      id: backupId,
      timestamp: new Date().toISOString(),
      description: `History data backup - ${new Date().toLocaleDateString()}`,
      adminActionsCount: Object.keys(historyData.adminActions).length,
      teacherHistoryCount: Object.keys(historyData.teacherHistory).length,
      studentHistoryCount: Object.keys(historyData.studentHistory).length,
      communicationLogsCount: historyData.communicationLogs.length,
      size: backupStr.length
    };
    
    backupsMeta.push(newBackupMeta);
    localStorage.setItem('history_backups_meta', JSON.stringify(backupsMeta));
    
    // Create file to download
    const filename = `nalc-history-backup-${new Date().toISOString().split('T')[0]}.json`;
    
    // Save to file system if on native platform
    if (Capacitor.isNativePlatform()) {
      try {
        // Save backup file to app documents directory
        await Filesystem.writeFile({
          path: `nalc_tracker/backups/${createUniqueFilename(filename)}`,
          data: backupStr,
          directory: Directory.Documents,
          recursive: true
        });
        toast.success('History backup saved to documents folder');
        await playNotificationSound();
      } catch (error) {
        console.error('Error saving history backup to filesystem:', error);
        // Fallback to browser download if native save fails
        const blob = new Blob([backupStr], { type: 'application/json' });
        saveAs(blob, filename);
      }
    } else {
      // Browser environment - use FileSaver
      const blob = new Blob([backupStr], { type: 'application/json' });
      saveAs(blob, filename);
      await playNotificationSound();
    }
    
    // Notify success with sound
    const totalCount = Object.keys(historyData.adminActions).length + 
      Object.keys(historyData.teacherHistory).length + 
      Object.keys(historyData.studentHistory).length;
    
    notifyBackupSuccess('history records', totalCount.toString());
    
    return backupId;
  } catch (error) {
    console.error('Error creating history backup:', error);
    toast.error('Failed to create history backup');
    throw error;
  }
};

/**
 * List all available history backups
 */
export const listHistoryBackups = (): Array<{
  id: string;
  timestamp: string;
  description: string;
  adminActionsCount: number;
  teacherHistoryCount: number;
  studentHistoryCount: number;
  communicationLogsCount: number;
  size: number;
}> => {
  try {
    const backupsMeta = JSON.parse(localStorage.getItem('history_backups_meta') || '[]');
    return backupsMeta.sort((a: any, b: any) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  } catch (error) {
    console.error('Error listing history backups:', error);
    return [];
  }
};

/**
 * Restore history data from a backup
 */
export const restoreHistoryBackup = async (backupId: string): Promise<boolean> => {
  try {
    // Get the backup data
    const backupKey = `history_backup_${backupId}`;
    const backupStr = localStorage.getItem(backupKey);
    
    if (!backupStr) {
      toast.error('Backup not found');
      return false;
    }
    
    const backupData = JSON.parse(backupStr);
    
    // Restore admin actions
    Object.entries(backupData.adminActions).forEach(([key, data]) => {
      localStorage.setItem(key, JSON.stringify(data));
    });
    
    // Restore teacher history
    Object.entries(backupData.teacherHistory).forEach(([key, data]) => {
      localStorage.setItem(key, JSON.stringify(data));
    });
    
    // Restore student history
    Object.entries(backupData.studentHistory).forEach(([key, data]) => {
      localStorage.setItem(key, JSON.stringify(data));
    });
    
    // Restore communication logs
    if (backupData.communicationLogs && Array.isArray(backupData.communicationLogs)) {
      localStorage.setItem('communication_logs', JSON.stringify(backupData.communicationLogs));
    }
    
    const totalCount = Object.keys(backupData.adminActions).length + 
      Object.keys(backupData.teacherHistory).length + 
      Object.keys(backupData.studentHistory).length;
      
    notifyRestoreSuccess('history records', totalCount.toString());
    return true;
  } catch (error) {
    console.error('Error restoring history backup:', error);
    toast.error('Failed to restore history backup');
    return false;
  }
};

/**
 * Restore from a file
 */
export const restoreHistoryBackupFromFile = async (file: File): Promise<boolean> => {
  try {
    // Read file contents
    const fileContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsText(file);
    });
    
    // Parse backup data
    const backupData = JSON.parse(fileContent);
    
    // Basic validation
    if (!backupData.adminActions && !backupData.teacherHistory && !backupData.studentHistory) {
      toast.error('Invalid history backup format');
      return false;
    }
    
    // Create a new backup ID
    const backupId = uuidv4();
    const backupKey = `history_backup_${backupId}`;
    
    // Store the backup for future access
    localStorage.setItem(backupKey, fileContent);
    
    // Restore the data
    return await restoreHistoryBackup(backupId);
  } catch (error) {
    console.error('Error restoring history backup from file:', error);
    toast.error('Failed to restore history backup from file');
    return false;
  }
};

/**
 * Import a history backup from a file
 */
export const importHistoryBackupFromFilesystem = async (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        if (event.target?.result) {
          const backupStr = event.target.result.toString();
          const backupData = JSON.parse(backupStr);
          
          // Basic validation
          if (!backupData.adminActions && !backupData.teacherHistory && !backupData.studentHistory) {
            toast.error('Invalid history backup format');
            resolve(false);
            return;
          }
          
          // Create a new backup ID
          const backupId = uuidv4();
          const backupKey = `history_backup_${backupId}`;
          
          // Store the backup
          localStorage.setItem(backupKey, backupStr);
          
          // Store metadata
          const backupsMeta = JSON.parse(localStorage.getItem('history_backups_meta') || '[]');
          const newBackupMeta = {
            id: backupId,
            timestamp: new Date().toISOString(),
            description: `Imported history backup - ${file.name}`,
            adminActionsCount: Object.keys(backupData.adminActions || {}).length,
            teacherHistoryCount: Object.keys(backupData.teacherHistory || {}).length,
            studentHistoryCount: Object.keys(backupData.studentHistory || {}).length,
            communicationLogsCount: (backupData.communicationLogs || []).length,
            size: backupStr.length
          };
          
          backupsMeta.push(newBackupMeta);
          localStorage.setItem('history_backups_meta', JSON.stringify(backupsMeta));
          
          toast.success('History backup imported successfully');
          resolve(true);
        } else {
          toast.error('Failed to read the file');
          resolve(false);
        }
      } catch (error) {
        console.error('Error importing history backup:', error);
        toast.error('Failed to import history backup: Invalid format');
        resolve(false);
      }
    };
    
    reader.onerror = () => {
      toast.error('Error reading the file');
      resolve(false);
    };
    
    reader.readAsText(file);
  });
};

/**
 * Restore from a local history backup
 */
export const restoreFromLocalHistoryBackup = async (backupId: string): Promise<boolean> => {
  return restoreHistoryBackup(backupId);
};
